import { useCallback, useMemo } from 'react';
import type { BrandSchema } from 'src/models/brand/schema';
import type { BrandsMap } from 'src/types/brand';
import { useBrands } from './useBrands';
export const DEFAULT_CATEGORY_ID = 0;

type Response = {
  data: BrandsMap[];
  isLoading: boolean;
  isError: boolean;
};

export const useBrandList = (searchText: string, categoryId: number): Response => {
  const { brands, isLoading, isError } = useBrands();
  const filterBrands = useCallback(
    (brands: BrandSchema[]) => brands.filter((brand) => brand.name.toLowerCase().includes(searchText.toLowerCase())),
    [searchText]
  );
  const data = useMemo(() => {
    const filteredBrandsBySearchText = brands ? filterBrands(brands) : [];
    const indexedBrands = brandDataByIndex(filteredBrandsBySearchText);
    const brandsDisplayOrder = generateBrandListOrder();
    const sortedIndexedBrands = brandsDisplayOrder.reduce((acc: Response['data'], key: string) => {
      if (indexedBrands[key]) {
        acc.push({ [key]: indexedBrands[key] });
      }
      return acc;
    }, []);
    return filterIndexedBrandsByCategory(sortedIndexedBrands, categoryId);
  }, [brands, categoryId, filterBrands]);
  return {
    data,
    isLoading,
    isError
  };
};

const brandDataByIndex = (brands: BrandSchema[]) =>
  brands.reduce((acc: { [index: string]: BrandSchema[] }, brand) => {
    if (!acc[brand.index]) {
      acc[brand.index] = [];
    }
    acc[brand.index].push(brand);
    return acc;
  }, {});

const generateBrandListOrder = (): string[] => {
  const charCodeOfA = 65;
  const alphabetCount = 26;
  const ckjCharStrokeMaxCount = 48;
  const ckjCharStrokeMinCount = 1;
  const otherCharIndexSymbol = '#';
  const letters = Array.from({ length: alphabetCount }, (_, i) => String.fromCharCode(charCodeOfA + i));
  const numbers = Array.from({ length: ckjCharStrokeMaxCount }, (_, i) => (i + ckjCharStrokeMinCount).toString());
  return [...letters, ...numbers, otherCharIndexSymbol];
};

const filterIndexedBrandsByCategory = (brands: { [index: string]: BrandSchema[] }[], categoryId: number) => {
  if (categoryId === DEFAULT_CATEGORY_ID) return brands;
  return brands
    .map((indexedBrands) => {
      const indexedFilteredBrands = indexedBrands[Object.keys(indexedBrands)[0]].filter((brand) =>
        brand.largeCategories.some((category) => category.id === categoryId)
      );
      return { [Object.keys(indexedBrands)[0]]: indexedFilteredBrands };
    })
    .filter((indexedFilteredBrand) => Object.values(indexedFilteredBrand)[0].length > 0);
};
